import { Link, useMatches } from 'react-router-dom';
import { Bell, CircleHelp, ClipboardPen, Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useQuickService } from '@/components/orders/quick-service-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useDashboard } from '@/hooks/queries';
import { useAuth } from '@/hooks/use-auth';

interface RouteHandle {
  crumb?: string;
}

/** Barra superior: botão do menu (celular), breadcrumb, ajuda e avisos. */
export function Topbar({ onMenu }: { onMenu: () => void }) {
  const matches = useMatches();
  const crumb = [...matches].reverse().find((m) => (m.handle as RouteHandle | undefined)?.crumb)?.handle as
    | RouteHandle
    | undefined;
  const { can } = useAuth();
  const dashboard = useDashboard();
  const ready = dashboard.data?.readyForPickup ?? 0;
  const approval = dashboard.data?.awaitingApproval ?? 0;
  const alerts = can('dashboard.read') ? ready + approval : 0;
  const quickService = useQuickService();

  return (
    <header className="no-print sticky top-0 z-30 flex h-[60px] items-center justify-between gap-3 border-b bg-white px-4 sm:h-[70px] sm:px-8">
      <button
        type="button"
        onClick={onMenu}
        className="grid h-9 w-9 place-items-center rounded-[10px] border text-[#5d6c70] lg:hidden"
        aria-label="Abrir menu"
      >
        <Menu className="h-[18px] w-[18px]" />
      </button>
      <nav aria-label="Você está em" className="mr-auto truncate text-[13px] text-muted-foreground">
        <Link to="/" className="rounded-sm hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">Oficina</Link> <span className="mx-2 text-[#bec7c7]">/</span>
        <strong className="text-foreground">{crumb?.crumb ?? 'Visão geral'}</strong>
      </nav>
      <div className="flex items-center gap-2.5">
        {can('orders.write') && (
          <Button type="button" size="sm" onClick={() => quickService.open()} aria-label="Registrar serviço">
            <ClipboardPen /> <span className="hidden sm:inline">Registrar serviço</span>
          </Button>
        )}
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="hidden h-9 w-9 place-items-center rounded-[10px] border text-[#5d6c70] sm:grid"
              aria-label="Ajuda"
            >
              <CircleHelp className="h-[17px] w-[17px]" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72 p-4 text-[13px]">
            <b className="mb-2 block">Ajuda rápida</b>
            <ul className="list-disc space-y-1 pl-4 text-muted-foreground">
              <li>Crie uma OS em “Nova ordem”; o equipamento pode ser cadastrado na própria tela.</li>
              <li>Peças e serviços vêm do catálogo — use “Cadastrar” para incluir um item novo.</li>
              <li>O status segue o fluxo da oficina; voltar etapas exige uma observação.</li>
              <li>Em dúvidas sobre o sistema, fale com o administrador da sua oficina.</li>
            </ul>
          </PopoverContent>
        </Popover>
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="relative grid h-9 w-9 place-items-center rounded-[10px] border text-[#5d6c70]"
              aria-label={`Avisos${alerts ? ` (${alerts})` : ''}`}
            >
              <Bell className="h-[17px] w-[17px]" />
              {alerts > 0 && (
                <span className="absolute -right-1 -top-1 grid h-4 min-w-4 place-items-center rounded-full bg-primary px-1 text-[10px] font-bold text-white">
                  {alerts}
                </span>
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72 p-2 text-[13px]">
            {alerts === 0 ? (
              <p className="p-3 text-muted-foreground">Você está em dia!</p>
            ) : (
              <ul>
                {ready > 0 && (
                  <li>
                    <Link to="/ordens?status=CONCLUIDO" className="block rounded-md p-3 hover:bg-muted">
                      <b>{ready} OS pronta(s) para retirada</b>
                      <span className="block text-xs text-muted-foreground">Avise os clientes.</span>
                    </Link>
                  </li>
                )}
                {approval > 0 && (
                  <li>
                    <Link to="/ordens?status=AGUARDANDO_APROVACAO" className="block rounded-md p-3 hover:bg-muted">
                      <b>{approval} orçamento(s) aguardando aprovação</b>
                      <span className="block text-xs text-muted-foreground">Cobre um retorno do cliente.</span>
                    </Link>
                  </li>
                )}
              </ul>
            )}
          </PopoverContent>
        </Popover>
      </div>
    </header>
  );
}
